import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from "react";
import { AppContext, apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Activity, Layers, Brain, Eye, BarChart3, 
  TrendingUp, TrendingDown, AlertTriangle, RefreshCw,
  Plus, X, Maximize2, Minimize2, ChevronDown, ChevronUp,
  Search, Grid3X3, LayoutGrid, Clock, Zap, Shield,
  ArrowLeft, Settings
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import GlobalSearch from "../components/GlobalSearch";

// Market Hours Configuration
const MARKET_HOURS = {
  'NSE': { start: { hour: 9, minute: 15 }, end: { hour: 15, minute: 30 } },
  'BSE': { start: { hour: 9, minute: 15 }, end: { hour: 15, minute: 30 } },
  'NFO': { start: { hour: 9, minute: 15 }, end: { hour: 15, minute: 30 } },
  'BFO': { start: { hour: 9, minute: 15 }, end: { hour: 15, minute: 30 } },
  'MCX': { start: { hour: 9, minute: 0 }, end: { hour: 23, minute: 54 } },
  'CDS': { start: { hour: 9, minute: 0 }, end: { hour: 17, minute: 0 } },
};

// Check if market is open for given exchange
const isMarketOpen = (exchange = 'NSE') => {
  const now = new Date();
  // Weekend check
  const dayOfWeek = now.getDay();
  if (dayOfWeek === 0 || dayOfWeek === 6) return false;
  
  // Convert to IST
  const istOffset = 5.5 * 60;
  const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
  const istMinutes = (utcMinutes + istOffset) % (24 * 60);
  const istHour = Math.floor(istMinutes / 60);
  const istMinute = istMinutes % 60;
  
  const hours = MARKET_HOURS[exchange] || MARKET_HOURS['NSE'];
  const startMinutes = hours.start.hour * 60 + hours.start.minute;
  const endMinutes = hours.end.hour * 60 + hours.end.minute;
  const currentMinutes = istHour * 60 + istMinute;
  
  // MCX has overnight session
  if (exchange === 'MCX' && endMinutes < startMinutes) {
    return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
  }
  
  return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
};

// Get exchange from symbol
const getExchangeFromSymbol = (symbol) => {
  if (!symbol) return 'NSE';
  const s = symbol.toUpperCase();
  
  if (['CRUDEOIL', 'NATURALGAS', 'GOLD', 'SILVER', 'COPPER', 'ZINC', 'LEAD', 'ALUMINIUM', 'NICKEL', 'COTTON'].some(p => s.includes(p))) {
    return 'MCX';
  }
  if (['SENSEX', 'BANKEX'].some(p => s.includes(p))) return 'BFO';
  if (['USDINR', 'EURINR', 'GBPINR', 'JPYINR'].some(p => s.includes(p))) return 'CDS';
  if (['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'].some(m => s.includes(m))) {
    return 'NFO';
  }
  return 'NSE';
};

// Market Status Banner - Shows all markets status
// Note: Zerodha provides 5-level market depth
const MarketStatusBanner = () => {
  const nseOpen = isMarketOpen('NSE');
  const mcxOpen = isMarketOpen('MCX');
  
  // For 5-level DOM (Zerodha), both NSE and MCX are supported
  const domAvailable = nseOpen || mcxOpen;
  
  // Determine overall status message
  let statusMsg = "ALL CLOSED";
  let statusColor = "text-red-400";
  let bgColor = "bg-red-900/30 border-red-500/30";
  
  if (nseOpen) {
    statusMsg = "NSE OPEN (5-Lvl Active)";
    statusColor = "text-green-400";
    bgColor = "bg-green-900/30 border-green-500/30";
  } else if (mcxOpen) {
    statusMsg = "MCX OPEN (5-Lvl Active)";
    statusColor = "text-amber-400";
    bgColor = "bg-amber-900/30 border-amber-500/30";
  }
  
  return (
    <div className={cn(
      "flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs border",
      bgColor
    )}>
      <div className={cn(
        "w-2 h-2 rounded-full", 
        dom20Available ? "bg-green-500 animate-pulse" : mcxOpen ? "bg-amber-500 animate-pulse" : "bg-red-500"
      )} />
      <span className={statusColor}>{statusMsg}</span>
      <span className="text-[#666]">|</span>
      <div className="flex gap-2 text-[9px]">
        <span className={nseOpen ? "text-green-400" : "text-[#555]"}>
          NSE 09:15-15:30 {nseOpen ? "(20-Lvl)" : ""}
        </span>
        <span className={mcxOpen ? "text-amber-400" : "text-[#555]"}>
          MCX 09:00-23:30 {mcxOpen ? "(5-Lvl)" : ""}
        </span>
      </div>
      {!dom20Available && mcxOpen && (
        <span className="text-[8px] text-[#666] ml-2">
          *20-level depth only for NSE
        </span>
      )}
    </div>
  );
};

// DOM Heatmap Cell
const HeatmapCell = ({ qty, maxQty, side, price }) => {
  const intensity = maxQty > 0 ? (qty / maxQty) * 100 : 0;
  const bgColor = side === 'buy' 
    ? `rgba(34, 197, 94, ${intensity / 100 * 0.8})` 
    : `rgba(239, 68, 68, ${intensity / 100 * 0.8})`;
  
  return (
    <div 
      className="h-5 flex items-center justify-between px-2 text-[9px] border-b border-[#1a1a1a]"
      style={{ backgroundColor: bgColor }}
    >
      <span className="text-white/90 font-mono">{price?.toFixed(2)}</span>
      <span className="text-white font-medium">{qty?.toLocaleString()}</span>
    </div>
  );
};

// Imbalance Indicator
const ImbalanceIndicator = ({ imbalance, compact = false }) => {
  if (!imbalance) return null;
  
  const { ratio, pressure, signal, total_bid_qty, total_ask_qty } = imbalance;
  const barWidth = Math.min(ratio || 50, 100);
  
  return (
    <div className={cn("bg-[#0a0a0a] rounded-lg border border-[#222]", compact ? "p-2" : "p-3")}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] text-[#666] uppercase tracking-wide">Order Book Imbalance</span>
        <span className={cn(
          "text-xs font-bold",
          pressure === 'buy' ? 'text-green-400' : pressure === 'sell' ? 'text-red-400' : 'text-[#666]'
        )}>
          {signal}
        </span>
      </div>
      <div className="relative h-4 bg-[#1a1a1a] rounded overflow-hidden">
        <div 
          className={cn(
            "absolute left-0 top-0 h-full transition-all duration-300",
            pressure === 'buy' ? 'bg-gradient-to-r from-green-600 to-green-400' : 
            pressure === 'sell' ? 'bg-gradient-to-r from-red-600 to-red-400' :
            'bg-gradient-to-r from-gray-600 to-gray-400'
          )}
          style={{ width: `${barWidth}%` }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[10px] font-bold text-white drop-shadow">
            {ratio}% BUY
          </span>
        </div>
      </div>
      {!compact && (
        <div className="flex justify-between mt-1 text-[9px] text-[#555]">
          <span>Buy: {total_bid_qty?.toLocaleString()}</span>
          <span>Sell: {total_ask_qty?.toLocaleString()}</span>
        </div>
      )}
    </div>
  );
};

// Large Order Alert
const LargeOrderAlert = ({ order }) => {
  return (
    <div className={cn(
      "flex items-center gap-2 p-2 rounded border animate-pulse",
      order.side === 'buy' 
        ? 'bg-green-900/30 border-green-500/50' 
        : 'bg-red-900/30 border-red-500/50'
    )}>
      <AlertTriangle className={cn(
        "w-3 h-3",
        order.side === 'buy' ? 'text-green-400' : 'text-red-400'
      )} />
      <div className="flex-1">
        <p className="text-[9px] text-white font-medium">{order.alert}</p>
        <p className="text-[8px] text-[#666]">Level {order.level}</p>
      </div>
    </div>
  );
};

// AI Signal Display
const AISignalDisplay = ({ signal, compact = false }) => {
  if (!signal) return null;
  
  const bgColor = signal.signal === 'STRONG_BUY' ? 'bg-green-900/50' :
                  signal.signal === 'BUY' ? 'bg-green-900/30' :
                  signal.signal === 'STRONG_SELL' ? 'bg-red-900/50' :
                  signal.signal === 'SELL' ? 'bg-red-900/30' :
                  'bg-[#1a1a1a]';
  
  return (
    <div className={cn("rounded-lg border border-[#333]", bgColor, compact ? "p-2" : "p-3")}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1">
          <Brain className="w-3 h-3 text-purple-400" />
          <span className="text-[9px] text-purple-400 uppercase">AI Signal</span>
        </div>
        <span className="text-sm">{signal.emoji}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className={cn(
          "text-xs font-bold",
          signal.signal?.includes('BUY') ? 'text-green-400' : 
          signal.signal?.includes('SELL') ? 'text-red-400' : 'text-[#888]'
        )}>
          {signal.signal?.replace('_', ' ')}
        </span>
        <span className="text-[9px] text-[#888]">{signal.confidence}%</span>
      </div>
      {!compact && <p className="text-[8px] text-[#666] mt-1">{signal.action}</p>}
    </div>
  );
};

// Order Flow Delta Display
const OrderFlowDelta = ({ orderFlow, compact = false }) => {
  if (!orderFlow) return null;
  
  const { cumulative_delta, buy_trades, sell_trades, trend } = orderFlow;
  
  return (
    <div className={cn("bg-[#0a0a0a] rounded-lg border border-[#222]", compact ? "p-2" : "p-3")}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] text-[#666] uppercase">Order Flow Delta</span>
        <span className={cn(
          "text-[9px] font-bold px-1.5 py-0.5 rounded",
          trend === 'BULLISH' ? 'bg-green-900/50 text-green-400' :
          trend === 'BEARISH' ? 'bg-red-900/50 text-red-400' :
          'bg-[#222] text-[#888]'
        )}>
          {trend}
        </span>
      </div>
      
      <div className="text-center mb-1">
        <span className={cn(
          compact ? "text-lg" : "text-xl",
          "font-bold font-mono",
          cumulative_delta > 0 ? 'text-green-400' :
          cumulative_delta < 0 ? 'text-red-400' : 'text-[#888]'
        )}>
          {cumulative_delta > 0 ? '+' : ''}{cumulative_delta?.toLocaleString()}
        </span>
      </div>
      
      {!compact && (
        <div className="grid grid-cols-2 gap-1 text-center">
          <div className="p-1 bg-green-900/20 rounded">
            <p className="text-green-400 font-bold text-xs">{buy_trades}</p>
            <p className="text-[8px] text-[#666]">Buy</p>
          </div>
          <div className="p-1 bg-red-900/20 rounded">
            <p className="text-red-400 font-bold text-xs">{sell_trades}</p>
            <p className="text-[8px] text-[#666]">Sell</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Single DOM Grid Cell
const DOMGridCell = ({ 
  symbol, 
  tickData, 
  domData, 
  onRemove, 
  isSelected, 
  onSelect, 
  compact = false,
  marketOpen = true 
}) => {
  const exchange = getExchangeFromSymbol(symbol);
  const isExchangeOpen = isMarketOpen(exchange);
  const ltp = tickData?.last_price || tickData?.ltp || 0;
  const change = tickData?.change || 0;
  const isPositive = change >= 0;
  
  const { bids = [], asks = [], imbalance = {}, large_orders = [], ai_signal = null, order_flow = {} } = domData || {};
  
  const maxQty = Math.max(...bids.map(b => b.qty || 0), ...asks.map(a => a.qty || 0), 1);
  
  // Show market closed overlay if exchange is closed
  if (!isExchangeOpen) {
    return (
      <div className={cn(
        "bg-[#0d0d0d] border rounded-lg overflow-hidden relative",
        isSelected ? "border-emerald-500" : "border-[#222]"
      )}>
        {/* Header */}
        <div className="h-8 flex items-center justify-between px-2 border-b border-[#222] bg-[#111]">
          <div className="flex items-center gap-2">
            <span className="font-medium text-white text-xs">{symbol}</span>
            <span className="text-[8px] px-1 py-0.5 bg-red-900/30 text-red-400 rounded">CLOSED</span>
          </div>
          <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => onRemove(symbol)}>
            <X className="w-3 h-3 text-red-400" />
          </Button>
        </div>
        
        {/* Market Closed Overlay */}
        <div className="h-[200px] flex items-center justify-center">
          <div className="text-center">
            <Clock className="w-8 h-8 text-[#444] mx-auto mb-2" />
            <p className="text-[#666] text-xs">Market Closed</p>
            <p className="text-[#444] text-[9px]">{exchange} trading hours ended</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className={cn(
        "bg-[#0d0d0d] border rounded-lg overflow-hidden cursor-pointer transition-all",
        isSelected ? "border-emerald-500 shadow-lg shadow-emerald-500/20" : "border-[#222] hover:border-[#444]"
      )}
      onClick={() => onSelect(symbol)}
      data-testid={`dom-cell-${symbol}`}
    >
      {/* Header */}
      <div className="h-8 flex items-center justify-between px-2 border-b border-[#222] bg-[#111]">
        <div className="flex items-center gap-2">
          <div className={cn("w-1.5 h-1.5 rounded-full", isPositive ? "bg-green-500" : "bg-red-500")} />
          <span className="font-medium text-white text-xs truncate max-w-[100px]">{symbol}</span>
          <span className={cn("text-xs font-mono", isPositive ? "text-green-400" : "text-red-400")}>
            {ltp?.toLocaleString()}
          </span>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-5 w-5 p-0 hover:bg-red-900/30" 
          onClick={(e) => { e.stopPropagation(); onRemove(symbol); }}
        >
          <X className="w-3 h-3 text-[#666] hover:text-red-400" />
        </Button>
      </div>
      
      {/* Large Order Alerts */}
      {large_orders.length > 0 && (
        <div className="px-2 py-1 border-b border-[#222]">
          {large_orders.slice(0, 1).map((order, i) => (
            <LargeOrderAlert key={i} order={order} />
          ))}
        </div>
      )}
      
      {/* Content Grid */}
      <div className="p-2 space-y-2">
        {/* Imbalance */}
        <ImbalanceIndicator imbalance={imbalance} compact={compact} />
        
        {/* Mini DOM Heatmap */}
        <div className="grid grid-cols-2 gap-1">
          {/* Bids */}
          <div className="bg-[#0a0a0a] rounded border border-[#222]">
            <div className="px-1 py-0.5 border-b border-[#222] bg-green-900/20">
              <span className="text-[8px] text-green-400">BUY ({bids.length})</span>
            </div>
            <div className="max-h-[80px] overflow-y-auto">
              {bids.slice(0, compact ? 5 : 8).map((bid, i) => (
                <HeatmapCell key={i} qty={bid.qty} maxQty={maxQty} side="buy" price={bid.price} />
              ))}
            </div>
          </div>
          
          {/* Asks */}
          <div className="bg-[#0a0a0a] rounded border border-[#222]">
            <div className="px-1 py-0.5 border-b border-[#222] bg-red-900/20">
              <span className="text-[8px] text-red-400">SELL ({asks.length})</span>
            </div>
            <div className="max-h-[80px] overflow-y-auto">
              {asks.slice(0, compact ? 5 : 8).map((ask, i) => (
                <HeatmapCell key={i} qty={ask.qty} maxQty={maxQty} side="sell" price={ask.price} />
              ))}
            </div>
          </div>
        </div>
        
        {/* AI Signal & Order Flow */}
        <div className="grid grid-cols-2 gap-1">
          <AISignalDisplay signal={ai_signal} compact />
          <OrderFlowDelta orderFlow={order_flow} compact />
        </div>
      </div>
    </div>
  );
};

// Search Result Item
const SearchResultItem = ({ instrument, onSelect }) => {
  const segment = instrument.segment || "";
  const instType = instrument.instrument_type || "EQ";
  
  let badgeColor = "bg-gray-600";
  let badgeLabel = instType;
  
  if (segment.includes("INDICES")) {
    badgeColor = "bg-purple-600";
    badgeLabel = "IDX";
  } else if (instType === "FUT" || instType?.includes("FUT")) {
    badgeColor = "bg-yellow-600";
    badgeLabel = "FUT";
  } else if (instType === "CE") {
    badgeColor = "bg-green-600";
    badgeLabel = "CE";
  } else if (instType === "PE") {
    badgeColor = "bg-red-600";
    badgeLabel = "PE";
  }
  
  return (
    <button
      onClick={() => onSelect(instrument)}
      className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#1a1a1a] text-left border-b border-[#222]"
    >
      <div className="flex items-center gap-2">
        <span className={cn("text-[9px] px-1.5 py-0.5 rounded font-medium text-white", badgeColor)}>
          {badgeLabel}
        </span>
        <div>
          <p className="text-xs font-medium text-white">{instrument.tradingsymbol}</p>
          <p className="text-[9px] text-[#666]">{instrument.exchange}</p>
        </div>
      </div>
      <Plus className="w-3 h-3 text-emerald-400" />
    </button>
  );
};

// Main DOM Analysis Component
export default function DOMAnalysis() {
  const { 
    tickData, 
    subscribedTokens, 
    connectedBroker,
    subscribeInstruments,
    unsubscribeInstruments,
    hasPremium
  } = useContext(AppContext);
  
  const [viewMode, setViewMode] = useState('grid'); // 'single', 'grid'
  const [selectedSymbols, setSelectedSymbols] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [instruments, setInstruments] = useState({});
  const [domDataMap, setDomDataMap] = useState({});
  const [compactMode, setCompactMode] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(1000);
  
  // Load saved symbols from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('dom_symbols');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSelectedSymbols(parsed);
        if (parsed.length > 0) setSelectedSymbol(parsed[0]);
      } catch (e) {
        console.error('Failed to load saved DOM symbols:', e);
      }
    }
  }, []);
  
  // Save symbols to localStorage
  useEffect(() => {
    if (selectedSymbols.length > 0) {
      localStorage.setItem('dom_symbols', JSON.stringify(selectedSymbols));
    }
  }, [selectedSymbols]);
  
  // Fetch instruments - Zerodha only
  const fetchInstruments = useCallback(async (exchange = "NSE") => {
    try {
      // Use Zerodha/Kite instruments (instrument_token format)
      const response = await apiClient.get(`/instruments?exchange=${exchange}`);
      const instMap = {};
      response.data.instruments.forEach(i => {
        instMap[i.instrument_token] = {
          ...i,
          broker: 'zerodha'
        };
      });
      setInstruments(prev => ({ ...prev, ...instMap }));
    } catch (error) {
      console.error(`Failed to fetch ${exchange} instruments:`, error);
    }
  }, []);
  
  // Search instruments - Zerodha only
  const searchInstruments = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      // Search Zerodha instruments
      const response = await apiClient.post("/instruments/search", { query });
      if (response.data?.instruments) {
        // Filter to only show futures and indices (most relevant for DOM)
        const filtered = response.data.instruments.filter(i => 
          i.instrument_type === 'FUT' || 
          i.segment?.includes('INDICES') ||
          i.instrument_type === 'CE' ||
          i.instrument_type === 'PE'
        ).map(i => ({
          ...i,
          broker: 'zerodha'
        })).slice(0, 15);
        setSearchResults(filtered);
      }
    } catch (error) {
      console.error("Search failed:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);
  
  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => searchInstruments(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchInstruments]);
  
  // Fetch REAL DOM data from backend API
  const fetchDOMData = useCallback(async (symbol) => {
    try {
      const exchange = getExchangeFromSymbol(symbol);
      const response = await apiClient.get(`/dom/depth/${symbol}?exchange=${exchange}`);
      
      if (response.data?.success) {
        const data = response.data;
        return {
          bids: data.depth?.bids || [],
          asks: data.depth?.asks || [],
          large_orders: data.large_orders || [],
          imbalance: data.imbalance || {},
          ai_signal: data.ai_signal || {},
          order_flow: data.order_flow || {},
          iceberg_alerts: [],
          ltp: data.ltp,
          is_20_level: data.depth?.is_20_level || false
        };
      }
      return null;
    } catch (error) {
      console.error(`Failed to fetch DOM data for ${symbol}:`, error);
      return null;
    }
  }, []);
  
  // Update DOM data periodically - REAL API CALLS
  useEffect(() => {
    if (selectedSymbols.length === 0) return;
    
    const updateDOMData = async () => {
      const newDomData = {};
      
      // Fetch DOM data for each symbol in parallel
      const promises = selectedSymbols.map(async (symbol) => {
        const exchange = getExchangeFromSymbol(symbol);
        const isExchangeOpen = isMarketOpen(exchange);
        
        if (!isExchangeOpen) {
          // Return empty data for closed markets
          newDomData[symbol] = {
            bids: [],
            asks: [],
            large_orders: [],
            imbalance: { ratio: 50, pressure: 'neutral', signal: 'MARKET CLOSED' },
            ai_signal: { signal: 'NEUTRAL', confidence: 0, action: 'Market closed' },
            order_flow: { cumulative_delta: 0, buy_trades: 0, sell_trades: 0, trend: 'NEUTRAL' },
            market_closed: true
          };
          return;
        }
        
        const domData = await fetchDOMData(symbol);
        if (domData) {
          newDomData[symbol] = domData;
        }
      });
      
      await Promise.all(promises);
      setDomDataMap(newDomData);
    };
    
    updateDOMData();
    const interval = setInterval(updateDOMData, refreshInterval);
    return () => clearInterval(interval);
  }, [selectedSymbols, fetchDOMData, refreshInterval]);
  
  // Load instruments on mount
  useEffect(() => {
    fetchInstruments("NSE");
    fetchInstruments("NFO");
    fetchInstruments("MCX");
  }, [fetchInstruments]);
  
  // Add symbol
  const handleAddSymbol = (instrument) => {
    const symbol = instrument.tradingsymbol || instrument.trading_symbol;
    const token = instrument.instrument_token || instrument.security_id;
    
    if (!symbol || !token) {
      toast.error("Invalid instrument");
      return;
    }
    
    if (selectedSymbols.includes(symbol)) {
      toast.info(`${symbol} already added`);
      return;
    }
    if (selectedSymbols.length >= 20) {
      toast.warning("Maximum 20 symbols allowed");
      return;
    }
    
    setSelectedSymbols(prev => [...prev, symbol]);
    if (!selectedSymbol) setSelectedSymbol(symbol);
    
    // Normalize instrument and subscribe
    const normalizedInstrument = {
      ...instrument,
      instrument_token: token,
      tradingsymbol: symbol
    };
    
    // Subscribe to instrument for tick data - Zerodha only
    subscribeInstruments([parseInt(token)]);
    
    setInstruments(prev => ({ ...prev, [token]: normalizedInstrument }));
    
    setSearchQuery('');
    setSearchResults([]);
    toast.success(`Added ${symbol}`);
  };
  
  // Remove symbol
  const handleRemoveSymbol = (symbol) => {
    setSelectedSymbols(prev => prev.filter(s => s !== symbol));
    if (selectedSymbol === symbol) {
      setSelectedSymbol(selectedSymbols.find(s => s !== symbol) || null);
    }
    toast.info(`Removed ${symbol}`);
  };
  
  // DOM Analysis works with Zerodha 5-level depth
  
  return (
    <div className="h-screen w-full flex flex-col bg-[#0A0A0A]" data-testid="dom-analysis-page">
      {/* Header */}
      <header className="h-14 border-b border-[#222] bg-[#111] px-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => window.history.back()}
            className="h-8 px-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-orange-400" />
            <span className="font-bold text-white">DOM Analysis</span>
            <span className="text-[10px] px-2 py-0.5 bg-orange-900/50 text-orange-400 rounded-full">
              5-Level (Zerodha)
            </span>
          </div>
          
          <MarketStatusBanner />
        </div>
        
        <div className="flex items-center gap-3">
          {/* Global Symbol Search */}
          <GlobalSearch
            onSelect={handleAddSymbol}
            placeholder="Add symbol (FUT, OPT)..."
            connectedBroker={connectedBroker}
            className="w-64"
            showExamples={false}
          />
          
          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 bg-[#0a0a0a] rounded-lg border border-[#333] p-0.5">
            <Button
              variant={viewMode === 'single' ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode('single')}
              className={cn("h-7 px-2", viewMode === 'single' && "bg-emerald-600")}
            >
              <Eye className="w-3 h-3 mr-1" />
              Single
            </Button>
            <Button
              variant={viewMode === 'grid' ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode('grid')}
              className={cn("h-7 px-2", viewMode === 'grid' && "bg-emerald-600")}
            >
              <Grid3X3 className="w-3 h-3 mr-1" />
              Grid
            </Button>
          </div>
          
          {/* Compact Mode */}
          <Button
            variant={compactMode ? "default" : "ghost"}
            size="sm"
            onClick={() => setCompactMode(!compactMode)}
            className={cn("h-7 px-2", compactMode && "bg-amber-600")}
          >
            <Minimize2 className="w-3 h-3 mr-1" />
            Compact
          </Button>
          
          {/* Refresh Rate */}
          <Select value={refreshInterval.toString()} onValueChange={(v) => setRefreshInterval(Number(v))}>
            <SelectTrigger className="w-24 h-8 bg-[#0a0a0a] border-[#333] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="500">500ms</SelectItem>
              <SelectItem value="1000">1s</SelectItem>
              <SelectItem value="2000">2s</SelectItem>
              <SelectItem value="5000">5s</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>
      
      {/* Main Content */}
      <div className="flex-1 overflow-auto p-4">
        {selectedSymbols.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <Layers className="w-16 h-16 text-[#333] mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Symbols Added</h3>
            <p className="text-[#888] text-sm mb-4">
              Search and add futures/options symbols to start DOM analysis
            </p>
            <div className="text-[#666] text-xs space-y-1">
              <p>Try: NIFTY, BANKNIFTY, CRUDEOIL</p>
              <p>Features: 20-Level DOM, Order Flow, AI Signals</p>
            </div>
          </div>
        ) : viewMode === 'single' ? (
          // Single View - Full DOM Panel
          <div className="grid grid-cols-4 gap-4 h-full">
            {/* Symbol List */}
            <div className="col-span-1 bg-[#111] border border-[#222] rounded-lg overflow-hidden">
              <div className="p-2 border-b border-[#222] bg-[#0a0a0a]">
                <span className="text-xs text-[#888]">Symbols ({selectedSymbols.length})</span>
              </div>
              <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                {selectedSymbols.map(symbol => {
                  const tick = Object.values(tickData).find(t => 
                    t.tradingsymbol === symbol || 
                    instruments[t.instrument_token]?.tradingsymbol === symbol
                  );
                  const ltp = tick?.last_price || tick?.ltp || 0;
                  const change = tick?.change || 0;
                  const isPositive = change >= 0;
                  
                  return (
                    <div 
                      key={symbol}
                      className={cn(
                        "flex items-center justify-between p-2 border-b border-[#1a1a1a] cursor-pointer",
                        selectedSymbol === symbol ? "bg-emerald-900/30 border-l-2 border-l-emerald-500" : "hover:bg-[#1a1a1a]"
                      )}
                      onClick={() => setSelectedSymbol(symbol)}
                    >
                      <div>
                        <p className="text-xs text-white font-medium">{symbol}</p>
                        <p className={cn("text-[10px] font-mono", isPositive ? "text-green-400" : "text-red-400")}>
                          {ltp?.toLocaleString()} ({isPositive ? '+' : ''}{change?.toFixed(2)})
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-5 w-5 p-0" 
                        onClick={(e) => { e.stopPropagation(); handleRemoveSymbol(symbol); }}
                      >
                        <X className="w-3 h-3 text-[#666] hover:text-red-400" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Full DOM Panel */}
            <div className="col-span-3">
              {selectedSymbol && (
                <DOMGridCell 
                  symbol={selectedSymbol}
                  tickData={Object.values(tickData).find(t => 
                    t.tradingsymbol === selectedSymbol || 
                    instruments[t.instrument_token]?.tradingsymbol === selectedSymbol
                  )}
                  domData={domDataMap[selectedSymbol]}
                  onRemove={handleRemoveSymbol}
                  isSelected={true}
                  onSelect={() => {}}
                  compact={false}
                />
              )}
            </div>
          </div>
        ) : (
          // Grid View
          <div className={cn(
            "grid gap-3",
            compactMode 
              ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" 
              : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          )}>
            {selectedSymbols.map(symbol => (
              <DOMGridCell 
                key={symbol}
                symbol={symbol}
                tickData={Object.values(tickData).find(t => 
                  t.tradingsymbol === symbol || 
                  instruments[t.instrument_token]?.tradingsymbol === symbol
                )}
                domData={domDataMap[symbol]}
                onRemove={handleRemoveSymbol}
                isSelected={selectedSymbol === symbol}
                onSelect={setSelectedSymbol}
                compact={compactMode}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <footer className="h-8 border-t border-[#222] bg-[#111] px-4 flex items-center justify-between text-[10px] flex-shrink-0">
        <div className="flex items-center gap-4">
          <span className="text-emerald-400">
            <Layers className="w-3 h-3 inline mr-1" />
            {selectedSymbols.length} symbol{selectedSymbols.length !== 1 ? 's' : ''}
          </span>
          <span className="text-[#666]">|</span>
          <span className="text-purple-400">
            <Brain className="w-3 h-3 inline mr-1" />
            AI Signals Active
          </span>
          <span className="text-[#666]">|</span>
          <span className="text-amber-400">
            <Zap className="w-3 h-3 inline mr-1" />
            Refresh: {refreshInterval}ms
          </span>
        </div>
        <div className="text-[#555]">
          Premium Feature • Zerodha 5-Level Market Depth
        </div>
      </footer>
    </div>
  );
}
